import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  TablePagination,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
  MonetizationOn as MoneyIcon,
  Stars as ActiveIcon,
} from '@mui/icons-material';

// components
import { Typography } from '../../../components/Wrappers/Wrappers';
import AlertModal from '../../../components/AlertModal';
import ConfirmModal from '../../../components/ConfirmModal';
import RefreshButton from '../../../components/RefreshButton/RefreshButton';

// services
import { incentiveGeneratorService } from '../../../services/incentiveGenerator';

const GlassCard = ({ children, sx, ...props }) => (
  <Card
    {...props}
    sx={{
      background: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const IncentiveApprove = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  
  // Modal states
  const [detailModal, setDetailModal] = useState({ open: false, data: null });
  const [approveModal, setApproveModal] = useState({ open: false, data: null, remark: '' });
  const [alertData, setAlertData] = useState({ open: false, message: '', type: 'info' });

  useEffect(() => {
    fetchIncentives();
  }, [page, rowsPerPage, search]);

  const handleRefresh = () => {
    fetchIncentives();
  };

  const fetchIncentives = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search,
        isApproved: false, // Explicitly requesting pending incentives
      };
      
      const response = await incentiveGeneratorService.getIncentives(params);
      
      if (response.success || response.status) {
        const dataList = response.data || [];
        setIncentives(dataList);
        setTotalCount(response.extra?.total || response.pagination?.total || dataList.length);
      } else {
        setIncentives([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching incentives:', error);
      setAlertData({ open: true, message: error.message || 'Failed to fetch incentive list', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approveModal.data) return;
    
    try {
      // Create flexible payload mapping to match backend expectations robustly
      const incentiveId = approveModal.data.incentiveGeneratorId || approveModal.data._id || approveModal.data.id;
      const payload = {
        id: incentiveId,
        _id: incentiveId,
        incentiveId: incentiveId,
        incentiveGeneratorId: incentiveId,
        remark: approveModal.remark
      };

      const response = await incentiveGeneratorService.approveIncentive(payload);

      if (response.success || response.status) {
        setAlertData({ open: true, message: 'Incentive approved successfully', type: 'success' });
        setApproveModal({ open: false, data: null, remark: '' });
        fetchIncentives();
      } else {
        setAlertData({ open: true, message: response.message || 'Failed to approve incentive', type: 'error' });
      }
    } catch (error) {
      console.error('Error approving incentive:', error);
      setAlertData({ open: true, message: error.message || 'Failed to complete incentive approval', type: 'error' });
    }
  };

  const getProgressPercentage = (achieved, target) => {
    if (!target || target === 0) return 100;
    return Math.min(100, Math.round((achieved / target) * 100));
  };

  const getProgressColor = (achieved, target) => {
    const percent = getProgressPercentage(achieved, target);
    if (percent >= 100) return theme.palette.success.main;
    if (percent >= 80) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Stats calculation
  const totalAmount = incentives.reduce((acc, curr) => acc + (curr.incentiveAmount || 0), 0);
  const pendingCount = incentives.length;

  return (
    <Box sx={{ pt: { xs: 1, md: 2 }, px: { xs: 2, md: 4 }, pb: { xs: 2, md: 4 }, minHeight: '100vh', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
      {/* Header */}
      <Box sx={{
        mb: 4,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', fontSize: { xs: '1.5rem', md: '2rem' }, mb: 0.5 }}>
            Incentive Approval
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Review and approve pending incentives for associates.
          </Typography>
        </Box>
        <RefreshButton onClick={handleRefresh} loading={loading} />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Pending Incentives', value: totalCount, icon: <ActiveIcon />, color: theme.palette.primary.main },
          { label: 'Page Pending Incentives', value: pendingCount, icon: <InfoIcon />, color: theme.palette.warning.main },
          { label: 'Page Pending Amount', value: `₹${totalAmount.toLocaleString('en-IN')}`, icon: <MoneyIcon />, color: theme.palette.success.main },
        ].map((stat, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <GlassCard>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                <Avatar sx={{ 
                  background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}99 100%)`,
                  color: '#fff',
                  width: 48, height: 48, 
                  borderRadius: '12px',
                  boxShadow: `0 4px 12px 0 ${stat.color}30`
                }}>
                  {stat.icon}
                </Avatar>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>{stat.label.toUpperCase()}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>{stat.value}</Typography>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      {/* Search Filter */}
      <GlassCard sx={{ mb: 4, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search associate name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.5)' } }}
            />
          </Grid>
        </Grid>
      </GlassCard>

      {/* Incentives List Table */}
      <Paper sx={{ 
        borderRadius: '16px', 
        overflow: 'hidden', 
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead sx={{ bgcolor: 'rgba(244, 247, 254, 0.5)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>ASSOCIATE</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>LEVEL & RANK</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>BUSINESS ACHIEVEMENT</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>INCENTIVE</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>STATUS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incentives.map((item) => (
                  <TableRow key={item._id || item.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' }, transition: '0.2s' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36, mr: 1.5, fontSize: '0.9rem', fontWeight: 800 }}>
                          {item.associateName?.charAt(0) || 'A'}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9rem' }}>{item.associateName}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Code: {item.associateCode || 'N/A'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.85rem' }}>
                        {item.levelCode || `L${item.level || ''}`}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{item.rankName || item.rank || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      {/* Team Business Progress */}
                      {(item.teamBusinessTarget !== undefined || item.minTeamBusiness !== undefined) && (
                        <Box sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>Team Business</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {getProgressPercentage(item.teamBusinessAchieved || item.teamBusiness || 0, item.teamBusinessTarget || item.minTeamBusiness || 0)}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={getProgressPercentage(item.teamBusinessAchieved || item.teamBusiness || 0, item.teamBusinessTarget || item.minTeamBusiness || 0)} 
                            sx={{ 
                              height: 6, 
                              borderRadius: 3, 
                              bgcolor: 'rgba(0,0,0,0.05)', 
                              '& .MuiLinearProgress-bar': { 
                                bgcolor: getProgressColor(item.teamBusinessAchieved || item.teamBusiness || 0, item.teamBusinessTarget || item.minTeamBusiness || 0) 
                              } 
                            }}
                          />
                        </Box>
                      )}
                      
                      {/* Self Business Progress */}
                      {(item.selfBusinessTarget !== undefined || item.minSelfBusiness !== undefined) && (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>Self Business</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {getProgressPercentage(item.selfBusinessAchieved || item.selfBusiness || 0, item.selfBusinessTarget || item.minSelfBusiness || 0)}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={getProgressPercentage(item.selfBusinessAchieved || item.selfBusiness || 0, item.selfBusinessTarget || item.minSelfBusiness || 0)} 
                            sx={{ 
                              height: 6, 
                              borderRadius: 3, 
                              bgcolor: 'rgba(0,0,0,0.05)', 
                              '& .MuiLinearProgress-bar': { 
                                bgcolor: getProgressColor(item.selfBusinessAchieved || item.selfBusiness || 0, item.selfBusinessTarget || item.minSelfBusiness || 0) 
                              } 
                            }}
                          />
                        </Box>
                      )}

                      {/* Fallback if no target is provided */}
                      {item.teamBusinessTarget === undefined && item.selfBusinessTarget === undefined && item.minTeamBusiness === undefined && item.minSelfBusiness === undefined && (
                        <Typography variant="caption" color="text.secondary">Business data not available</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem' }}>
                        {item.percentage !== undefined ? `${item.percentage}%` : ''}
                      </Typography>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>
                        ₹{item.incentiveAmount?.toLocaleString('en-IN') || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.isApproved ? 'APPROVED' : 'PENDING'} 
                        size="small"
                        sx={{ 
                          fontWeight: 800, 
                          fontSize: '0.65rem',
                          bgcolor: item.isApproved ? `${theme.palette.success.main}15` : `${theme.palette.warning.main}15`,
                          color: item.isApproved ? theme.palette.success.main : theme.palette.warning.main,
                          border: `1px solid ${item.isApproved ? theme.palette.success.main : theme.palette.warning.main}30`
                        }} 
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton onClick={() => setDetailModal({ open: true, data: item })} size="small" color="primary">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        {!item.isApproved && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setApproveModal({ open: true, data: item, remark: '' })}
                            sx={{ 
                              borderRadius: '8px', 
                              textTransform: 'none', 
                              fontWeight: 700,
                              bgcolor: theme.palette.success.main,
                              '&:hover': { bgcolor: theme.palette.success.dark }
                            }}
                          >
                            Approve
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {incentives.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">No pending incentives found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 15]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      {/* Approve Modal */}
      <Dialog open={approveModal.open} onClose={() => setApproveModal({ ...approveModal, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Approve Incentive</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Are you sure you want to approve the incentive of <strong>₹{approveModal.data?.incentiveAmount?.toLocaleString('en-IN')}</strong> for <strong>{approveModal.data?.associateName}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Remarks (Optional)"
            placeholder="Add any internal remarks here..."
            value={approveModal.remark}
            onChange={(e) => setApproveModal({ ...approveModal, remark: e.target.value })}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setApproveModal({ ...approveModal, open: false })}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success" sx={{ fontWeight: 700 }}>Confirm Approval</Button>
        </DialogActions>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModal.open} onClose={() => setDetailModal({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon color="primary" /> Incentive Details
        </DialogTitle>
        <DialogContent dividers>
          {detailModal.data && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}><PersonIcon /></Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{detailModal.data.associateName}</Typography>
                    <Typography variant="body2" color="text.secondary">Code: {detailModal.data.associateCode || 'N/A'}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>LEVEL & RANK</Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {detailModal.data.levelCode || `L${detailModal.data.level || ''}`} - {detailModal.data.rankName || detailModal.data.rank || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>ACHIEVEMENT DATE</Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {detailModal.data.achievementDate ? new Date(detailModal.data.achievementDate).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ height: '1px', bgcolor: 'divider', my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>TEAM BUSINESS</Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  ₹{(detailModal.data.teamBusinessAchieved || detailModal.data.teamBusiness || 0).toLocaleString('en-IN')} 
                  {detailModal.data.teamBusinessTarget || detailModal.data.minTeamBusiness ? ` / ₹${(detailModal.data.teamBusinessTarget || detailModal.data.minTeamBusiness || 0).toLocaleString('en-IN')}` : ''}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>SELF BUSINESS</Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  ₹{(detailModal.data.selfBusinessAchieved || detailModal.data.selfBusiness || 0).toLocaleString('en-IN')} 
                  {detailModal.data.selfBusinessTarget || detailModal.data.minSelfBusiness ? ` / ₹${(detailModal.data.selfBusinessTarget || detailModal.data.minSelfBusiness || 0).toLocaleString('en-IN')}` : ''}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 2 }}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 800 }}>INCENTIVE PAYMENT DETAILS</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
                    ₹{detailModal.data.incentiveAmount?.toLocaleString('en-IN') || 0}
                  </Typography>
                  {detailModal.data.percentage !== undefined && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Percentage Applied: <strong>{detailModal.data.percentage}%</strong>
                    </Typography>
                  )}
                  {detailModal.data.benefitType && (
                    <Typography variant="body2">
                      Benefit Type: <strong>{detailModal.data.benefitType}</strong>
                    </Typography>
                  )}
                  {detailModal.data.isRoyaltyApplicable !== undefined && (
                    <Typography variant="body2">
                      Royalty Applicable: <strong>{detailModal.data.isRoyaltyApplicable ? 'Yes' : 'No'}</strong>
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailModal({ open: false, data: null })} variant="contained" fullWidth>Close</Button>
        </DialogActions>
      </Dialog>

      <AlertModal open={alertData.open} message={alertData.message} type={alertData.type} onClose={() => setAlertData({ ...alertData, open: false })} />
    </Box>
  );
};

export default IncentiveApprove;
